import React, { useState, useMemo } from 'react';
import { DeleteConfirmationDialog } from '../../shared/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useRBAC } from '../context/RBACContext';
import styles from './MilestoneMenu.module.scss';

interface MilestoneDeleteButtonProps {
  milestones: any[];
  onDelete?: (phaseId?: string, phaseName?: string) => void;
  phaseId?: string;
  phaseName?: string;
  isOpen: boolean;
  onToggle?: () => void;
  onInternalClose: () => void;
}

export const MilestoneDeleteButton: React.FC<MilestoneDeleteButtonProps> = ({
  milestones,
  onDelete,
  phaseId,
  phaseName,
  isOpen,
  onToggle,
  onInternalClose
}) => {
  // Get permissions from RBAC context
  const permissions = useRBAC();
  const canEdit = permissions?.canEdit ?? false;

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Check if any milestone has a status (indicating it has been edited with data)
  // Using JSON.stringify to create a deep dependency on milestone content
  const milestonesString = JSON.stringify(milestones?.map(m => m.status));

  const hasAnyMilestoneData = useMemo(() => {
    if (!milestones || milestones.length === 0) {
      return false;
    }

    return milestones.some((m: any) => {
      // Check if status field has non-empty data (not null, undefined, or empty string)
      return m.status !== null;
    });
  }, [milestonesString]); // ← Deep comparison via stringified statuses

  // Close the menu (handles both controlled and internal state)
  const closeMenu = () => {
    if (onToggle && isOpen) {
      onToggle();
    } else if (!onToggle) {
      onInternalClose();
    }
  };

  // Handle silent deletion of empty milestones (no confirmation needed)
  const handleSilentDelete = () => {
    // TODO: Add actual delete logic here
    if (onDelete) {
      onDelete(phaseId, phaseName);
    }

    // Close menu after silent delete
    closeMenu();
  };

  // Handle delete confirmation (when user confirms deletion of milestones WITH data)
  const handleConfirmDelete = () => {
    // TODO: Add actual delete logic here
    if (onDelete) {
      onDelete(phaseId, phaseName);
    }
    setShowDeleteConfirmation(false);
    // Close menu after confirmation
    closeMenu();
  };

  // Handle delete cancellation
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    // Close menu after cancellation
    closeMenu();
  };

  const handleDeleteMilestones = () => {
    // Prevent execution if no delete handler
    if (!canEdit || !onDelete || milestones.length === 0) {
      return;
    }
    // Check if milestones have data (status field)
    if (hasAnyMilestoneData) {
      // Milestones have data - show confirmation dialog
      // Note: Keep menu open while dialog is shown, close after user responds
      setShowDeleteConfirmation(true);
    } else {
      // Milestones are empty - delete silently
      handleSilentDelete();
    }
  };

  return (
    <>
      <button
        className={styles.menuItem}
        onClick={handleDeleteMilestones}
        disabled={!canEdit || !onDelete || milestones.length === 0}
        data-testid="delete-milestone-option"
      >
        <span className={styles.menuItemText}>Delete Milestones</span>
      </button>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        title="Delete Milestones?"
        message="Are you sure you want to delete all milestones in this phase? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default MilestoneDeleteButton;
